import { styled } from "@mui/material/styles";
import React, { Fragment, ReactNode } from "react";

import {
    ProductAdditionListNode,
    ProductAttributeListNode,
    ProductDataCancellationPolicy,
} from "../apiHooks/useProduct";
import { BulletedList } from "../components/BulletedList";
import { ReadMoreContainer } from "../components/ExpandableContent";
import { FadeUpAnimationWrapper } from "../components/FadeUpAnimationWrapper";
import { HTMLContent } from "../components/HTMLContent";
import { TextBody } from "../components/Text";
import { ProductContentType } from "../types/ProductContent";
import { ProductContentSection, ProductContentSectionType } from "./ProductContentSection";
import { ProductContentSections } from "./ProductContentSections";
import {
    ProductFullWrapper,
    ProductFullMainLayout,
    ProductMainLayoutMode,
    ProductFullGalleryWrapper,
    ProductFullSideContentWrapper,
    ProductFullSideContentStyled,
    ProductFullMainContentWrapper,
} from "./ProductFullLayoutComponents";
import { ProductGallery, ProductGalleryProps } from "./ProductGallery";
import { ProductHeader, ProductHeaderProps } from "./ProductHeader";
import { ProductHeaderSection } from "./ProductHeaderSection";
import { ProductMeta } from "./ProductMeta";
import { ProductReviewsInfo } from "./ProductReviewsInfo";
import { ProductSectionAdditions } from "./ProductSection/ProductSectionAdditions";
import { ProductSectionCancellationPolicy } from "./ProductSection/ProductSectionCancellationPolicy";
import { ProductSectionGuideLanguages } from "./ProductSection/ProductSectionGuideLanguages";
import { ProductSectionStartTimes } from "./ProductSection/ProductSectionStartTimes";
import { ProductMeta as ProductMetaType, ProductContent, ProductContentEntry } from "./types";

export type ProductProps = Pick<ProductHeaderProps, "title" | "minPrice"> &
    Partial<Pick<ProductGalleryProps, "imageList">> & {
        content?: ProductContent;
        attributeList?: ProductAttributeListNode[];
        meta: ProductMetaType;
        additions?: ProductAdditionListNode[];
        cancellationPolicy?: ProductDataCancellationPolicy;
        sideElement?: React.ReactNode;
        belowHeaderElement?: React.ReactNode;
        reviewRating: number | null;
        reviewCount: number | null;
        layoutMode?: ProductMainLayoutMode;
        showHeader?: boolean;
        constrainWidth?: boolean;
        withStickyAppBar?: boolean;
        withSectionFadeUpAnimation?: boolean;
        showFavouriteButton?: boolean;
        isFavourite?: boolean;
        onFavouriteChange?: (isFavourite: boolean) => void;
        children?: ReactNode;
    };

export function ProductFull({
    title,
    minPrice,
    attributeList,
    additions,
    cancellationPolicy,
    imageList,
    content,
    meta,
    sideElement,
    belowHeaderElement,
    reviewRating,
    reviewCount,
    layoutMode = "default",
    showHeader = true,
    constrainWidth = true,
    withStickyAppBar = false,
    withSectionFadeUpAnimation = false,
    showFavouriteButton,
    isFavourite,
    onFavouriteChange,
    children,
}: ProductProps) {
    return (
        <>
            <ProductHeaderSection
                showHeader={showHeader}
                title={title}
                minPrice={minPrice}
                withStickyAppBar={withStickyAppBar}
                ProductHeaderComponent={ProductHeader}
            />
            <ProductFullWrapper constraindWidth={constrainWidth}>
                {belowHeaderElement ? <BelowHeaderWrapper>{belowHeaderElement}</BelowHeaderWrapper> : undefined}
                <ProductFullMainLayout layoutMode={layoutMode}>
                    <ProductFullGalleryWrapper>
                        {imageList && (
                            <ProductGallery
                                imageList={imageList}
                                showFavouriteButton={showFavouriteButton}
                                isFavourite={isFavourite}
                                onFavouriteChange={onFavouriteChange}
                            />
                        )}
                    </ProductFullGalleryWrapper>
                    <ProductFullSideContentWrapper>
                        {sideElement ? (
                            <ProductFullSideContentStyled>{sideElement}</ProductFullSideContentStyled>
                        ) : undefined}
                    </ProductFullSideContentWrapper>

                    <ProductFullMainContentWrapper>
                        <ProductMetaStyled meta={meta} attributeList={attributeList} />
                        <ProductContentSections>
                            {reviewRating && reviewCount && (
                                <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
                                    <ProductReviewsInfo rating={reviewRating} reviewsNumber={reviewCount} />{" "}
                                </FadeUpAnimationWrapper>
                            )}
                            {content && (
                                <ProductContentSectionsWrapper
                                    content={content}
                                    withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                />
                            )}
                            {additions && additions.length > 0 && <ProductSectionAdditions additions={additions} />}
                            {meta.guideLanguages && meta.guideLanguages.length > 0 && (
                                <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
                                    <ProductSectionGuideLanguages languages={meta.guideLanguages} />
                                </FadeUpAnimationWrapper>
                            )}
                            {meta.startTimes && meta.startTimes.length > 0 && (
                                <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
                                    <ProductSectionStartTimes startTimes={meta.startTimes} />
                                </FadeUpAnimationWrapper>
                            )}
                            {cancellationPolicy && (
                                <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
                                    <ProductSectionCancellationPolicy cancellationPolicy={cancellationPolicy} />
                                </FadeUpAnimationWrapper>
                            )}
                        </ProductContentSections>
                    </ProductFullMainContentWrapper>
                </ProductFullMainLayout>
                {children}
            </ProductFullWrapper>
        </>
    );
}

const BelowHeaderWrapper = styled("div")(({ theme }) => ({
    margin: theme.spacing(2, 0),
    "&:empty": {
        display: "none",
    },
}));

const ProductMetaStyled = styled(ProductMeta)(({ theme }) => ({
    marginBottom: theme.spacing(4),
}));

const OTHER_CONTENT_TYPES: Array<{
    type: ProductContentType & ProductContentSectionType;
    component: React.ComponentType<ProductContentBaseProps>;
}> = [
    { type: "HIGHLIGHT", component: ProductContentBasic },
    { type: "ITINERARY", component: ProductContentBasic },
    { type: "INCLUSION", component: ProductContentBasic },
    { type: "EXCLUSION", component: ProductContentBasic },
    { type: "VENUE", component: ProductContentBasic },
    { type: "EXHIBITION", component: ProductContentBasic },
    { type: "OPENING_HOURS", component: ProductContentBulletedList },
    { type: "AVAILABLE_DAY", component: ProductContentBasic },
    { type: "SAFETY_MEASURE", component: ProductContentBasic },
    { type: "MEETING_POINT", component: ProductContentBasic },
    { type: "FINISHING_POINT", component: ProductContentBasic },
    { type: "NOTE", component: ProductContentBulletedList },
    { type: "TIMING_NOTE", component: ProductContentBasic },
    { type: "LOCATION_NOTE", component: ProductContentBasic },
    { type: "REQUIREMENT", component: ProductContentBasic },
];

function ProductContentSectionsWrapper({
    content: { root, other },
    withSectionFadeUpAnimation,
}: {
    content: ProductContent;
    withSectionFadeUpAnimation?: boolean;
}) {
    const sections: ReactNode[] = [];

    if (root?.description) {
        sections.push(
            <FadeUpAnimationWrapper key={"about"} isEnabled={withSectionFadeUpAnimation}>
                <ProductContentSection type={"ROOT"} titleTranslationKey={"about"}>
                    <HTMLContent content={root.description} />
                </ProductContentSection>
            </FadeUpAnimationWrapper>
        );
    }

    for (const { type, component: Component } of OTHER_CONTENT_TYPES) {
        const content = other.get(type);
        if (content) {
            sections.push(
                <FadeUpAnimationWrapper key={type} isEnabled={withSectionFadeUpAnimation}>
                    <ProductContentSection type={type}>
                        <Component entries={content} />
                    </ProductContentSection>
                </FadeUpAnimationWrapper>
            );
        }
    }

    return <>{sections}</>;
}

type ProductContentBaseProps = {
    entries: ProductContentEntry[];
};

function ProductContentBasic({ entries }: ProductContentBaseProps) {
    return (
        <ReadMoreContainer>
            {entries.map(({ name, description }, i) => {
                if (!name) {
                    return <HTMLContent content={description} key={i} />;
                }

                return (
                    <Fragment key={name + i}>
                        <TextBody>{name}</TextBody>
                        <HTMLContent content={description} variant={"caption"} />
                    </Fragment>
                );
            })}
        </ReadMoreContainer>
    );
}

function ProductContentBulletedList({ entries }: ProductContentBaseProps) {
    return (
        <>
            <ReadMoreContainer>
                <BulletedList>
                    {entries.map(({ name, description }, i) => {
                        if (!name) {
                            return (
                                <li key={i}>
                                    <HTMLContent content={description} key={i} />
                                </li>
                            );
                        }

                        return (
                            <li key={i}>
                                <TextBody>{name}</TextBody>
                                <HTMLContent content={description} variant={"caption"} />
                            </li>
                        );
                    })}
                </BulletedList>
            </ReadMoreContainer>
        </>
    );
}
